
import { useMemo } from 'react';
import { FilterOption } from '../TableFilters';

interface SmartMeter {
  deviceId: string;
  location: string;
  status: string;
  batteryLevel: number;
  signalStrength: number;
  lastCommunication: string;
  firmwareVersion: string;
  readings: {
    voltage: number;
    current: number;
    frequency: number;
    power: number;
    totalEnergy: number;
  };
  maintenanceStatus: {
    lastMaintenance: string;
    nextScheduled: string;
    alerts: string[];
  };
}

export const useSmartMeterFilters = (
  smartMeters: SmartMeter[],
  activeFilters: Record<string, string[]>
) => {
  // Generate filter categories based on the data
  const filterCategories = useMemo(() => {
    // Get unique locations
    const locations = [...new Set(smartMeters.map(meter => meter.location))].map(location => ({
      value: location,
      label: location,
      count: smartMeters.filter(meter => meter.location === location).length
    }));

    // Status options
    const statuses = [...new Set(smartMeters.map(meter => meter.status))].map(status => ({
      value: status,
      label: status,
      count: smartMeters.filter(meter => meter.status === status).length
    }));

    // Battery level ranges
    const batteryLevels: FilterOption[] = [
      { value: 'high', label: 'High (70-100%)', count: smartMeters.filter(m => m.batteryLevel > 70).length },
      { value: 'medium', label: 'Medium (30-70%)', count: smartMeters.filter(m => m.batteryLevel > 30 && m.batteryLevel <= 70).length },
      { value: 'low', label: 'Low (0-30%)', count: smartMeters.filter(m => m.batteryLevel <= 30).length }
    ];

    // Signal strength ranges
    const signalStrengths: FilterOption[] = [
      { value: 'strong', label: 'Strong (70-100%)', count: smartMeters.filter(m => m.signalStrength > 70).length },
      { value: 'moderate', label: 'Moderate (30-70%)', count: smartMeters.filter(m => m.signalStrength > 30 && m.signalStrength <= 70).length },
      { value: 'weak', label: 'Weak (0-30%)', count: smartMeters.filter(m => m.signalStrength <= 30).length }
    ];

    return [
      { name: 'Location', options: locations },
      { name: 'Status', options: statuses },
      { name: 'BatteryLevel', options: batteryLevels },
      { name: 'SignalStrength', options: signalStrengths }
    ];
  }, [smartMeters]);

  // Apply filters to the data
  const filteredMeters = useMemo(() => {
    return smartMeters.filter(meter => {
      // Check location filter
      if (activeFilters.Location.length > 0 && !activeFilters.Location.includes(meter.location)) {
        return false;
      }
      
      // Check status filter
      if (activeFilters.Status.length > 0 && !activeFilters.Status.includes(meter.status)) {
        return false;
      }
      
      // Check battery level filter
      if (activeFilters.BatteryLevel.length > 0) {
        const batteryMatch = activeFilters.BatteryLevel.some(level => {
          if (level === 'high') return meter.batteryLevel > 70;
          if (level === 'medium') return meter.batteryLevel > 30 && meter.batteryLevel <= 70;
          if (level === 'low') return meter.batteryLevel <= 30;
          return false;
        });
        if (!batteryMatch) return false;
      }
      
      // Check signal strength filter
      if (activeFilters.SignalStrength.length > 0) {
        const signalMatch = activeFilters.SignalStrength.some(signal => {
          if (signal === 'strong') return meter.signalStrength > 70;
          if (signal === 'moderate') return meter.signalStrength > 30 && meter.signalStrength <= 70;
          if (signal === 'weak') return meter.signalStrength <= 30;
          return false;
        });
        if (!signalMatch) return false;
      }
      
      return true;
    });
  }, [smartMeters, activeFilters]);

  return {
    filterCategories,
    filteredMeters
  };
};
